import React from 'react';

const formatDateTimeCompact = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const time = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  return `${day} ${month}, ${time}`;
};

/** Safe `dateTime` for `<time>`; avoids `RangeError` from `toISOString()` on Invalid Date. */
const toIsoDateTimeAttr = (dateStr) => {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const getRawTimestamp = (data) =>
  data?.created_at ||
  data?.createdAt ||
  data?.date ||
  data?.updated_at ||
  data?.updatedAt;

const stageTitle = (data) =>
  (data?.name && String(data.name).trim()) ||
  (data?.title && String(data.title).trim()) ||
  'Stage update';

const normalize = (s) =>
  String(s || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** Human-readable status line (aligned with deal-stage wording where overlap exists). */
const statusLabel = (status) => {
  if (!status || typeof status !== 'string') return null;
  const s = status.toLowerCase();
  const map = {
    pending: 'Pending',
    completed: 'Completed',
    due: 'Due',
    sign_pending: 'Sign pending'
  };
  return map[s] || status;
};

const ActivityCard = ({ data, isLatest, isLast }) => {
  const title = stageTitle(data);
  const rawDesc = (data?.description && String(data.description).trim()) || '';
  const showDescription = rawDesc && normalize(rawDesc) !== normalize(title);

  const primary = (data?.primaryExtra && String(data.primaryExtra).trim()) || '';
  const secondary = (data?.secondaryExtra && String(data.secondaryExtra).trim()) || '';
  const hasExtras = Boolean(primary || secondary);
  const extrasDuplicateDesc =
    hasExtras &&
    (normalize(primary) === normalize(rawDesc) || normalize(secondary) === normalize(rawDesc));

  const loggedAt = getRawTimestamp(data);
  const statusText = statusLabel(data?.status);
  const paymentStage = (data?.payment_stage_name && String(data.payment_stage_name).trim()) || '';

  const timelineIconClass = isLatest
    ? 'bg-gray-200 border-2 border-teal-600/40 text-white shadow-md ring-2 ring-white'
    : 'border-2 border-teal-600/40 bg-white text-teal-700 shadow-sm ring-2 ring-white';

  return (
    <div className={`relative flex w-full min-w-0 gap-3 ${isLast ? 'pb-0' : 'pb-10'}`}>
      <div className="relative z-[1] flex w-11 flex-shrink-0 flex-col items-center self-stretch">
        <div
          className={`relative z-[2] mt-7 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${timelineIconClass}`}
          aria-hidden>
          <img
            src={isLatest ? '/icons/document.svg' : '/icons/organisation.svg'}
            alt=""
            className="h-[18px] w-[18px] object-contain"
          />
        </div>
        {/* Oldest stage is end of story (newest-first list): hide spine below last node — left column
            stretches with the tall card, so the global line would otherwise run past the icon. */}
        {isLast && (
          <div
            className="relative z-[2] min-h-[12px] w-full flex-1 bg-white"
            aria-hidden
          />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <time
          dateTime={toIsoDateTimeAttr(loggedAt)}
          className="block text-xs font-medium tracking-wide text-gray-600">
          {formatDateTimeCompact(loggedAt)}
        </time>

        <div className="overflow-hidden rounded-xl border border-gray-200/90 bg-[#f8fafb] px-4 py-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="text-[15px] font-semibold leading-snug text-[#2A565E]">{title}</h3>
            <div className="flex flex-wrap items-center gap-1.5">
              {isLatest && (
                <span className="rounded-full bg-teal-700/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal-800">
                  Current
                </span>
              )}
              {statusText && (
                <span className="rounded-full bg-gray-200/80 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                  {statusText}
                </span>
              )}
            </div>
          </div>

          {paymentStage && (
            <p className="mt-2 text-xs font-medium text-gray-600">
              Payment stage: <span className="text-gray-800">{paymentStage}</span>
            </p>
          )}

          {showDescription && (
            <p className="mt-3 text-sm leading-relaxed text-gray-700">{rawDesc}</p>
          )}

          {hasExtras && !extrasDuplicateDesc && (
            <div className="mt-3 rounded-lg border border-teal-700/10 bg-white/80 px-3 py-2.5">
              {primary && <p className="text-sm font-medium text-gray-800">{primary}</p>}
              {secondary && (
                <p className={`text-sm text-gray-600 ${primary ? 'mt-1' : ''}`}>{secondary}</p>
              )}
            </div>
          )}

          {data?.createdBy?.name && (
            <p className="mt-3 text-xs text-gray-500">
              <span className="text-gray-400">Updated by </span>
              <span className="font-medium text-gray-700">{data.createdBy.name}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;
